import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import SEOHead from "@/components/SEOHead";
import logoFull from "@/assets/logo.png";
import logoIcon from "@/assets/logo-icon.png";

const ConfirmEmail = () => {
  const [email, setEmail] = useState("");
  const [resending, setResending] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const { toast } = useToast();

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => setCountdown((c) => c - 1), 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  const handleResend = async () => {
    if (!email) {
      toast({ title: "Enter your email", variant: "destructive" });
      return;
    }
    setResending(true);
    const { error } = await supabase.auth.resend({
      type: "signup",
      email,
      options: { emailRedirectTo: "https://aibly.club" },
    });
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Email sent", description: "Check your inbox again." });
      setCountdown(60);
    }
    setResending(false);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <SEOHead title="Confirm Email — aibly" description="Verify your email address to activate your aibly subscription and start receiving the daily AI briefing." canonical="/confirm-email" noindex />
      <div className="w-full max-w-sm space-y-8 text-center">
        <div className="flex items-center justify-center mb-6">
          <img src={logoIcon} alt="aibly logo" width={500} height={500} className="h-16 w-auto dark:invert sm:hidden" />
          <img src={logoFull} alt="aibly logo" width={486} height={135} className="h-10 w-auto dark:invert hidden sm:block" />
        </div>

        <div className="space-y-3">
          <p className="text-[10px] tracking-[0.4em] uppercase text-accent font-mono">Step 02 of 02</p>
          <h1 className="font-display text-3xl italic text-foreground">
            Check your inbox.
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            We sent a confirmation link. One click and your daily briefing is live.
          </p>
        </div>

        {/* Spam warning */}
        <div className="border-y border-accent/30 py-4 space-y-2 text-left">
          <p className="text-[10px] font-mono tracking-[0.4em] uppercase text-accent">
            One housekeeping note
          </p>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Our confirmation may land in <strong className="text-foreground">Spam</strong> or <strong className="text-foreground">Junk</strong>. Mark it "Not Spam" so future editions hit your inbox.
          </p>
        </div>

        <div className="border border-border p-6 space-y-4">
          <p className="text-xs tracking-widest uppercase text-muted-foreground">
            Didn't get it?
          </p>
          <Input
            aria-label="Email address for confirmation resend"
            type="email"
            placeholder="your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="rounded-none bg-secondary border-border text-foreground placeholder:text-muted-foreground text-sm"
          />
          <Button
            onClick={handleResend}
            disabled={resending || countdown > 0}
            variant="outline"
            className="w-full rounded-none text-xs tracking-widest uppercase"
          >
            {resending
              ? "Sending..."
              : countdown > 0
              ? `Resend in ${Math.floor(countdown / 60)}:${(countdown % 60).toString().padStart(2, "0")}`
              : "Resend confirmation"}
          </Button>
        </div>

        <Link
          to="/login"
          className="inline-block text-xs text-muted-foreground hover:text-foreground transition-colors tracking-wider uppercase"
        >
          Back to login
        </Link>
      </div>
    </div>
  );
};

export default ConfirmEmail;
